import { FormEvent, useEffect, useState } from 'react';
import AccountLayout from './components/AccountLayout';
import PasswordInput from '@/components/feature/PasswordInput';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { MemberApiError, NewsletterStatus, getNewsletterStatus, subscribeNewsletter, unsubscribeNewsletterAsMember } from '@/services/memberAuth';
import { getDistrictsByCity, getPostalCode, taiwanCities } from '@/data/taiwanAddress';

const inputClass =
  'w-full px-3.5 py-2.5 rounded-xl border border-[#E8E0D6] text-base outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]';
const selectClass = inputClass + ' bg-white';

const currentYear = new Date().getFullYear();
const birthYears = Array.from({ length: 100 }, (_, i) => String(currentYear - i));
const birthMonths = Array.from({ length: 12 }, (_, i) => String(i + 1));
const birthDays = Array.from({ length: 31 }, (_, i) => String(i + 1));

/** 會員資料的 BirthDate 是 "YYYY-MM-DD" 字串（或 null），拆成年/月/日給下拉選單使用，跟註冊表單同一套元件。 */
function splitBirthDate(value?: string | null): { year: string; month: string; day: string } {
  if (!value) return { year: '', month: '', day: '' };
  const [year, month, day] = value.split('-');
  return {
    year: year || '',
    month: month ? String(Number(month)) : '',
    day: day ? String(Number(day)) : '',
  };
}

export default function AccountProfile() {
  const { member, updateProfile, changePassword } = useMemberAuth();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState<'Male' | 'Female' | ''>('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressDistrict, setAddressDistrict] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const addressDistricts = getDistrictsByCity(addressCity);
  const addressPostalCode = getPostalCode(addressCity, addressDistrict);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});

  // 表單欄位刻意不用 useState(member?.X) 當初始值：AccountLayout 的登入驗證是非同步的，
  // 這個元件有可能在 member 資料還沒抓回來之前就先掛載一次，若只靠 useState 的初始值，
  // 之後 member 補上資料時欄位也不會再更新（useState 的初始值只在第一次渲染生效）。
  // 改用 useEffect 監聽 member 變化來同步表單，才能確保不管掛載時機為何，資料一到就會正確帶入。
  useEffect(() => {
    if (!member) return;
    setName(member.Name || '');
    setPhone(member.Phone || '');
    setGender((member.Gender as 'Male' | 'Female' | '') || '');
    const parsedBirthDate = splitBirthDate(member.BirthDate);
    setBirthYear(parsedBirthDate.year);
    setBirthMonth(parsedBirthDate.month);
    setBirthDay(parsedBirthDate.day);
    setAddressCity(member.AddressCity || '');
    setAddressDistrict(member.AddressDistrict || '');
    setAddressLine(member.AddressLine || '');
  }, [member]);

  function handleAddressCityChange(city: string) {
    setAddressCity(city);
    setAddressDistrict('');
  }

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [newsletter, setNewsletter] = useState<NewsletterStatus | null>(null);
  const [newsletterLoading, setNewsletterLoading] = useState(true);
  const [newsletterSaving, setNewsletterSaving] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');

  useEffect(() => {
    let alive = true;
    getNewsletterStatus()
      .then((data) => {
        if (alive) setNewsletter(data.Newsletter);
      })
      .catch(() => {
        if (alive) setNewsletterError('電子報訂閱狀態載入失敗，請重新整理再試。');
      })
      .finally(() => {
        if (alive) setNewsletterLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function handleNewsletterToggle() {
    setNewsletterSaving(true);
    setNewsletterError('');
    try {
      const data = newsletter?.Subscribed ? await unsubscribeNewsletterAsMember() : await subscribeNewsletter();
      setNewsletter(data.Newsletter);
    } catch (err) {
      setNewsletterError(err instanceof Error ? err.message : '操作失敗，請稍後再試。');
    } finally {
      setNewsletterSaving(false);
    }
  }

  async function handleProfileSubmit(event: FormEvent) {
    event.preventDefault();
    setProfileMessage('');
    setProfileError('');
    setProfileErrors({});

    if (phone.trim() && !/^09\d{8}$/.test(phone.trim())) {
      setProfileErrors({ Phone: '請輸入正確的手機號碼格式（09 開頭，共 10 碼）。' });
      return;
    }

    setProfileSaving(true);
    try {
      await updateProfile({
        Name: name.trim(),
        Phone: phone.trim() || undefined,
        Gender: gender || undefined,
        BirthYear: birthYear || undefined,
        BirthMonth: birthMonth || undefined,
        BirthDay: birthDay || undefined,
        AddressCity: addressCity || undefined,
        AddressDistrict: addressDistrict || undefined,
        AddressPostalCode: addressPostalCode || undefined,
        AddressLine: addressLine.trim() || undefined,
      });
      setProfileMessage('會員資料已更新。');
    } catch (err) {
      if (err instanceof MemberApiError) {
        setProfileError(err.message);
        setProfileErrors(err.errors || {});
      } else {
        setProfileError(err instanceof Error ? err.message : '更新失敗，請稍後再試。');
      }
    } finally {
      setProfileSaving(false);
    }
  }

  async function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    setPasswordMessage('');
    setPasswordError('');
    if (newPassword !== confirmNewPassword) {
      setPasswordError('兩次輸入的新密碼不一致。');
      return;
    }
    setPasswordSaving(true);
    try {
      await changePassword({ CurrentPassword: currentPassword, NewPassword: newPassword });
      setPasswordMessage('密碼已更新。');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : '更新失敗，請稍後再試。');
    } finally {
      setPasswordSaving(false);
    }
  }

  return (
    <AccountLayout title="會員資料">
      <div className="grid gap-6">
        <form onSubmit={handleProfileSubmit} className="space-y-4 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6">
          <div>
            <label className="block text-sm font-medium text-[#1B3D2A] mb-1.5">Email</label>
            <input value={member?.Email || ''} disabled className={`${inputClass} bg-white text-[#9A8A7A]`} />
          </div>
          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">姓名</label>
            <input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="profile-phone" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">聯絡電話</label>
            <input id="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0912345678" className={inputClass} />
            {profileErrors.Phone && <p className="mt-1 text-xs text-[#c3423f]">{profileErrors.Phone}</p>}
          </div>

          <div>
            <span className="block text-sm font-medium text-[#1B3D2A] mb-1.5">性別（選填）</span>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-[#1B3D2A]">
                <input type="radio" name="profile-gender" value="Male" checked={gender === 'Male'} onChange={() => setGender('Male')} className="h-4 w-4 accent-[#1B3D2A]" />
                男
              </label>
              <label className="flex items-center gap-2 text-sm text-[#1B3D2A]">
                <input type="radio" name="profile-gender" value="Female" checked={gender === 'Female'} onChange={() => setGender('Female')} className="h-4 w-4 accent-[#1B3D2A]" />
                女
              </label>
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-[#1B3D2A] mb-1.5">生日（選填）</span>
            <div className="grid grid-cols-3 gap-3">
              <select value={birthYear} onChange={(e) => setBirthYear(e.target.value)} className={selectClass} aria-label="出生年">
                <option value="">請選擇</option>
                {birthYears.map((year) => (
                  <option key={year} value={year}>{year} 年</option>
                ))}
              </select>
              <select value={birthMonth} onChange={(e) => setBirthMonth(e.target.value)} className={selectClass} aria-label="出生月">
                <option value="">請選擇</option>
                {birthMonths.map((month) => (
                  <option key={month} value={month}>{month} 月</option>
                ))}
              </select>
              <select value={birthDay} onChange={(e) => setBirthDay(e.target.value)} className={selectClass} aria-label="出生日">
                <option value="">請選擇</option>
                {birthDays.map((day) => (
                  <option key={day} value={day}>{day} 日</option>
                ))}
              </select>
            </div>
            {profileErrors.BirthDate && <p className="mt-1 text-xs text-[#c3423f]">{profileErrors.BirthDate}</p>}
          </div>

          <div>
            <span className="block text-sm font-medium text-[#1B3D2A] mb-1.5">聯絡地址（選填）</span>
            <p className="mb-2 text-xs text-[#9A8A7A]">結帳時可自動帶入此地址，不用每次重新輸入；仍可在結帳頁隨時修改。</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.2fr_1.2fr_1fr]">
              <select value={addressCity} onChange={(e) => handleAddressCityChange(e.target.value)} className={selectClass} aria-label="縣市">
                <option value="">請選擇縣市</option>
                {taiwanCities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <select
                value={addressDistrict}
                onChange={(e) => setAddressDistrict(e.target.value)}
                className={selectClass}
                aria-label="鄉鎮市區"
                disabled={!addressCity}
              >
                <option value="">{addressCity ? '請選擇鄉鎮市區' : '請先選擇縣市'}</option>
                {addressDistricts.map((district) => (
                  <option key={district.name} value={district.name}>{district.name}</option>
                ))}
              </select>
              <input value={addressPostalCode} readOnly placeholder="郵遞區號" className={`${inputClass} bg-white text-[#9A8A7A]`} />
            </div>
            <input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} placeholder="詳細地址" className={`${inputClass} mt-3`} />
          </div>

          {profileMessage && <p className="text-sm font-medium text-[#1B3D2A]">{profileMessage}</p>}
          {profileError && <p className="text-sm font-medium text-[#c3423f]">{profileError}</p>}
          <button
            type="submit"
            disabled={profileSaving}
            className="rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026] disabled:opacity-60"
          >
            {profileSaving ? '儲存中...' : '儲存變更'}
          </button>
        </form>

        <form onSubmit={handlePasswordSubmit} className="space-y-4 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6">
          <h3 className="text-base font-bold text-[#1B3D2A]">修改密碼</h3>
          <div>
            <label htmlFor="current-password" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">目前密碼</label>
            <PasswordInput id="current-password" value={currentPassword} onChange={setCurrentPassword} className={inputClass} />
          </div>
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">新密碼</label>
            <PasswordInput id="new-password" minLength={8} value={newPassword} onChange={setNewPassword} placeholder="至少 8 碼" className={inputClass} />
          </div>
          <div>
            <label htmlFor="confirm-new-password" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">確認新密碼</label>
            <PasswordInput id="confirm-new-password" minLength={8} value={confirmNewPassword} onChange={setConfirmNewPassword} className={inputClass} />
          </div>
          {passwordMessage && <p className="text-sm font-medium text-[#1B3D2A]">{passwordMessage}</p>}
          {passwordError && <p className="text-sm font-medium text-[#c3423f]">{passwordError}</p>}
          <button
            type="submit"
            disabled={passwordSaving}
            className="rounded-xl border border-[#1B3D2A] px-6 py-3 text-sm font-bold text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white disabled:opacity-60"
          >
            {passwordSaving ? '更新中...' : '更新密碼'}
          </button>
        </form>

        <div className="space-y-3 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6">
          <h3 className="text-base font-bold text-[#1B3D2A]">電子報訂閱狀態</h3>
          {newsletterLoading ? (
            <p className="text-sm text-[#9A8A7A]">載入中...</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-[#5A6B5A]">
                    {newsletter?.Subscribed ? (
                      <>
                        目前已訂閱電子報{newsletter.SubscribedAt ? `（訂閱時間：${newsletter.SubscribedAt}）` : ''}，我們會不定期寄送優惠活動與最新消息。
                      </>
                    ) : (
                      '目前尚未訂閱電子報，訂閱後可收到最新優惠活動與消息通知。'
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleNewsletterToggle}
                  disabled={newsletterSaving}
                  className={
                    newsletter?.Subscribed
                      ? 'shrink-0 rounded-xl border border-[#1B3D2A] px-5 py-2.5 text-sm font-bold text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white disabled:opacity-60'
                      : 'shrink-0 rounded-xl bg-[#1B3D2A] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#143026] disabled:opacity-60'
                  }
                >
                  {newsletterSaving ? '處理中...' : newsletter?.Subscribed ? '取消訂閱' : '訂閱電子報'}
                </button>
              </div>
              {newsletterError && <p className="text-sm font-medium text-[#c3423f]">{newsletterError}</p>}
            </>
          )}
        </div>
      </div>
    </AccountLayout>
  );
}
